import * as Yup from "yup";

export const ISSUE_TYPE_MIN_LENGTH = 3;
export const ISSUE_TYPE_MAX_LENGTH = 100;

export const ISSUE_TYPE_FORBIDDEN_CHARS_PATTERN = /[@#$%^&*<>]/;
export const ISSUE_TYPE_PATTERN = /^[a-zA-Z0-9\s\-]+$/;

export const IE_ISSUE_MESSAGES = {
  issueTypeRequired: "Please enter Issue Type.",
  kpiTypeRequired: "Please select KPI Type.",
  statusRequired: "Status is required.",
  issueTypeMin: "Minimum 3 characters required.",
  issueTypeMax: "Maximum 100 characters allowed.",
  issueTypeDuplicate: "Issue Type already exists.",
  issueTypeSpecialChars:
    "Special characters such as @, #, $, %, ^, &, *, <, > shall not be allowed.",
  createSuccess: "Issue Type created successfully.",
  updateSuccess: "Issue Type updated successfully.",
  saveFailed: "Unable to save Issue Type.",
};

export const formatIssueTypeInput = (value = "") =>
  value
    .slice(0, ISSUE_TYPE_MAX_LENGTH)
    .replace(/^\s+/, "")
    .replace(/\s{2,}/g, " ")
    .replace(/[^a-zA-Z0-9\s\-]/g, "");

const getErrorText = (data, fallback = "") => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  if (data?.Message) return data.Message;
  return fallback;
};

const getCurrentIssueId = (isEditMode, editRow) =>
  isEditMode
    ? editRow?.ieIssueId ??
      editRow?.ieIssueTypeId ??
      editRow?.issueTypeId ??
      editRow?.IeIssueId ??
      editRow?.id
    : null;

export const isDuplicateIssueType = (
  issueType,
  kpiTypeId,
  existingIssues = [],
  currentId = null
) => {
  const trimmed = issueType?.trim().toLowerCase();
  if (!trimmed || !kpiTypeId) return false;

  return existingIssues.some((item) => {
    const itemIssueType = (
      item.issueType ??
      item.IssueType ??
      ""
    )
      .trim()
      .toLowerCase();
    const itemKpiId =
      item.kpiTypeId ?? item.ieKpiTypeId ?? item.KpiTypeId;
    const itemId =
      item.ieIssueId ??
      item.IeIssueId ??
      item.ieIssueTypeId ??
      item.issueTypeId ??
      item.id;

    return (
      itemIssueType === trimmed &&
      String(itemKpiId) === String(kpiTypeId) &&
      String(itemId ?? "") !== String(currentId ?? "")
    );
  });
};

export const mapIeIssueBackendErrors = (data, fallbackMessage = "") => {
  const message = getErrorText(data, fallbackMessage);
  const text = message.toLowerCase();
  const errors = {};

  if (!text && !data?.errors) return errors;

  if (text.includes("already exist") || text.includes("duplicate")) {
    errors.issueType = IE_ISSUE_MESSAGES.issueTypeDuplicate;
  } else if (text.includes("kpi type") || text.includes("kpitype")) {
    errors.kpiTypeId = IE_ISSUE_MESSAGES.kpiTypeRequired;
  } else if (text.includes("issue type") || text.includes("issuetype")) {
    errors.issueType = message || IE_ISSUE_MESSAGES.issueTypeRequired;
  } else if (text.includes("status") || text.includes("active")) {
    errors.isActive = IE_ISSUE_MESSAGES.statusRequired;
  }

  if (data?.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([key, value]) => {
      const fieldMap = {
        issuetype: "issueType",
        issuetypeid: "issueType",
        kpiTypeid: "kpiTypeId",
        kpitypeid: "kpiTypeId",
        iekpitypeid: "kpiTypeId",
        isactive: "isActive",
      };
      const field = fieldMap[key.toLowerCase()] || key;
      const fieldMessage = Array.isArray(value) ? value[0] : value;
      errors[field] = fieldMessage;
    });
  }

  if (!Object.keys(errors).length && message) {
    const lower = message.toLowerCase();
    if (lower.includes("already exist") || lower.includes("duplicate")) {
      errors.issueType = IE_ISSUE_MESSAGES.issueTypeDuplicate;
    }
  }

  return errors;
};

export const getIeIssueValidationSchema = ({
  existingIssues = [],
  isEditMode = false,
  editRow = null,
}) => {
  const currentId = getCurrentIssueId(isEditMode, editRow);

  return Yup.object().shape({
    issueType: Yup.string()
      .trim()
      .required(IE_ISSUE_MESSAGES.issueTypeRequired)
      .min(ISSUE_TYPE_MIN_LENGTH, IE_ISSUE_MESSAGES.issueTypeMin)
      .max(ISSUE_TYPE_MAX_LENGTH, IE_ISSUE_MESSAGES.issueTypeMax)
      .test(
        "no-special-chars",
        IE_ISSUE_MESSAGES.issueTypeSpecialChars,
        (value) =>
          !value ||
          (!ISSUE_TYPE_FORBIDDEN_CHARS_PATTERN.test(value) &&
            ISSUE_TYPE_PATTERN.test(value))
      )
      .test(
        "unique-issue-type",
        IE_ISSUE_MESSAGES.issueTypeDuplicate,
        function (value) {
          const { kpiTypeId } = this.parent;
          if (!value || !kpiTypeId) return true;
          return !isDuplicateIssueType(
            value,
            kpiTypeId,
            existingIssues,
            currentId
          );
        }
      ),
    kpiTypeId: Yup.string().required(IE_ISSUE_MESSAGES.kpiTypeRequired),
    isActive: Yup.string().required(IE_ISSUE_MESSAGES.statusRequired),
  });
};

export const filterActiveKpiTypes = (kpiTypes = []) =>
  kpiTypes.filter((item) => item?.isActive !== false);
